import React, { useState } from 'react';
import ClinicLayout from './ClinicLayout';
import { ChevronDown, ChevronRight } from 'lucide-react';

const ClinicManageOffice = () => {
  const [expandedSections, setExpandedSections] = useState(['main', 'company', 'list-maintenance', 'appointment', 'eprescribe', 'pay-bill']);
  const [selectedItem, setSelectedItem] = useState(null);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => 
      prev.includes(sectionId) 
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const menuItems = {
    company: [
      'Company Information',
      'Signed Agreements',
      'Change Password',
      'Import Data Wizard',
      'Audit Log Reports (New)',
      'Audit Log Reports',
      'Preferences',
      'Patient Ally Settings',
      'Automated Eligibility Settings',
      'Intake/Agreement Forms',
    ],
    'list-maintenance': [
      'Medication Samples',
      'Offices',
      'Chart of Accounts',
      'Billing Providers',
      'Merge Patient Records',
      'Providers',
      'Staff',
      'Referring Providers',
      'Facilities',
      'Insurances',
      'Superbills',
      'National Drug Codes (NDC)',
      'CPT Codes',
      'Diagnosis Codes',
      'Reason for Visit',
      'Product Inventory',
    ],
    appointment: [
      'Time Schedules',
      'Special Dates',
    ],
    eprescribe: [
      'Pharmacies',
    ],
    'pay-bill': [
      'Pay Bill',
      'Statement / Invoices',
    ],
  };

  return (
    <ClinicLayout activeTab="manage-office">
      <div className="p-4 pr-20" data-testid="clinic-manage-office">
        {/* Sub Navigation Tabs */}
        <div className="flex items-center justify-end mb-2">
          <div className="flex items-center gap-4 text-xs">
            <span className="text-[#2563eb] cursor-pointer hover:underline">News & Updates</span>
            <span className="text-[#2563eb] cursor-pointer hover:underline">Feedback</span>
            <span className="text-[#2563eb] cursor-pointer hover:underline">Help</span>
            <span className="text-[#2563eb] cursor-pointer hover:underline">About</span>
          </div>
        </div>

        {/* Section Header */}
        <div className="bg-gradient-to-b from-[#4a5568] to-[#2d3748] text-white px-3 py-2 text-sm font-medium mb-4">
          Manage Office
        </div>

        {/* Content Area */}
        <div className="flex gap-4">
          {/* Left Tree Menu */}
          <div className="w-64 border border-gray-300 bg-white">
            <div className="p-2 text-xs">
              {/* Main */}
              <div className="mb-1">
                <button
                  onClick={() => toggleSection('main')}
                  className="flex items-center gap-1 font-medium"
                >
                  {expandedSections.includes('main') ? (
                    <ChevronDown className="w-3 h-3" />
                  ) : (
                    <ChevronRight className="w-3 h-3" />
                  )}
                  Main
                </button>

                {expandedSections.includes('main') && (
                  <div className="ml-3 mt-1">
                    {/* Company Settings */}
                    <div className="mb-1">
                      <button
                        onClick={() => toggleSection('company')}
                        className="flex items-center gap-1"
                      >
                        {expandedSections.includes('company') ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        Company Settings
                      </button>
                      {expandedSections.includes('company') && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {menuItems.company.map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-1 cursor-pointer hover:text-[#2563eb] ${selectedItem === item ? 'text-[#2563eb] font-medium' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* List Maintenance */}
                    <div className="mb-1">
                      <button
                        onClick={() => toggleSection('list-maintenance')}
                        className="flex items-center gap-1"
                      >
                        {expandedSections.includes('list-maintenance') ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        List Maintenance
                      </button>
                      {expandedSections.includes('list-maintenance') && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {menuItems['list-maintenance'].map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-1 cursor-pointer hover:text-[#2563eb] ${selectedItem === item ? 'text-[#2563eb] font-medium' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Appointment Settings */}
                    <div className="mb-1">
                      <button
                        onClick={() => toggleSection('appointment')}
                        className="flex items-center gap-1"
                      >
                        {expandedSections.includes('appointment') ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        Appointment Settings
                      </button>
                      {expandedSections.includes('appointment') && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {menuItems.appointment.map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-1 cursor-pointer hover:text-[#2563eb] ${selectedItem === item ? 'text-[#2563eb] font-medium' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* ePrescribe */}
                    <div className="mb-1">
                      <button
                        onClick={() => toggleSection('eprescribe')}
                        className="flex items-center gap-1"
                      >
                        {expandedSections.includes('eprescribe') ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        ePrescribe
                      </button>
                      {expandedSections.includes('eprescribe') && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {menuItems.eprescribe.map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-1 cursor-pointer hover:text-[#2563eb] ${selectedItem === item ? 'text-[#2563eb] font-medium' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Pay Office Ally Bill */}
                    <div className="mb-1">
                      <button
                        onClick={() => toggleSection('pay-bill')}
                        className="flex items-center gap-1"
                      >
                        {expandedSections.includes('pay-bill') ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        Pay Ivory Clinic Bill
                      </button>
                      {expandedSections.includes('pay-bill') && (
                        <div className="ml-4 mt-1 space-y-0.5">
                          {menuItems['pay-bill'].map((item, idx) => (
                            <div 
                              key={idx}
                              className={`flex items-center gap-1 cursor-pointer hover:text-[#2563eb] ${selectedItem === item ? 'text-[#2563eb] font-medium' : ''}`}
                              onClick={() => setSelectedItem(item)}
                            >
                              <ChevronRight className="w-3 h-3 text-gray-400" />
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Content Area */}
          <div className="flex-1 border border-gray-300 bg-white p-4 min-h-[400px]">
            {selectedItem ? (
              <div>
                <h3 className="text-lg font-medium mb-4">{selectedItem}</h3>
                <p className="text-gray-500 text-sm">Configure {selectedItem} settings here.</p>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Please select an item on the left tree to start.</p>
            )}
          </div>
        </div>
      </div>
    </ClinicLayout>
  );
};

export default ClinicManageOffice;
